import React, { useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { updateCarrier } from "../../../graphql/mutations";
import { reloadYardCarrierByCarrierByActive } from "../../../graphql/queries";
import {
  sanitizeCarrier,
  setActiveUserReloadYardCarriers,
} from "../../../graphql/carrier/carrierCRUD";

import { DialogContentText } from "@material-ui/core";
import ArchiveIcon from "@material-ui/icons/Archive";

import { Button, Confirm, useNotify, useRedirect } from "react-admin";
import { useFormState } from "react-final-form";

import { RollbackIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import { invalidateCarrierList } from "../../../utils/carrierListCache";

const archiveMessage = (
  <DialogContentText>
    This will deactivate the carrier company and revoke access for all associated
    users. This can be restored at any time.
    <br />
    Are you sure you want to archive this carrier?
  </DialogContentText>
);

// Toggles Carrier.active between "false" (archived) and "true" (active).
// Archive: sets Carrier.active="false" AND cascades active="false" to all active
//   CarrierUserReloadYardCarrier junction records for this carrier.
// Restore: sets Carrier.active="true" AND cascades active="true" back to junctions
//   that were archived (deleted != true). Explicitly deleted users are NOT restored.
// The deleted flag on CarrierUserReloadYardCarrier distinguishes between
//   "archived with carrier" (deleted=null) and "explicitly deleted" (deleted=true).
// For permanent removal (no shipment history), use DeepDeleteButton instead.
export const ArchiveRestoreButton = ({ record, ...props }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { pristine } = useFormState();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!record?.id) return null;

  const isArchived = record.active === "false";

  const handleClick = () => {
    if (isArchived) {
      handleRestore();
    } else {
      setOpen(true);
    }
  };

  const handleDialogClose = () => setOpen(false);

  const fetchJunctionsByActive = async (activeValue, filter) => {
    let all = [];
    let nextToken;
    do {
      // eslint-disable-next-line no-await-in-loop
      const response = await API.graphql(
        graphqlOperation(reloadYardCarrierByCarrierByActive, {
          carrierId: record.id,
          active: { eq: activeValue },
          filter,
          limit: 250,
          nextToken,
        }),
      );
      const page = response?.data?.reloadYardCarrierByCarrierByActive;
      if (page?.items?.length > 0) all = all.concat(page.items);
      nextToken = page?.nextToken;
    } while (nextToken);
    return all;
  };

  const handleArchive = async () => {
    setLoading(true);
    try {
      const activeUsers = await fetchJunctionsByActive("true");

      if (activeUsers.length > 0) {
        await Promise.all(setActiveUserReloadYardCarriers(activeUsers, "false"));
      }

      const carrierInput = sanitizeCarrier({ ...record, active: "false" });
      await API.graphql(graphqlOperation(updateCarrier, { input: carrierInput }));
      // The carrier dropdown caches the active list; without this it goes on
      // offering the carrier that was just archived.
      invalidateCarrierList();
      notify("Carrier archived successfully.");
      redirect("/carriers");
    } catch (error) {
      logError("managerApp:carriers:ArchiveRestoreButton:handleArchive", error, notify);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const handleRestore = async () => {
    setLoading(true);
    try {
      // Only restore junctions that were archived — skip explicitly deleted users
      const archivedUsers = await fetchJunctionsByActive("false", {
        deleted: { ne: true },
      });

      // Carrier must be committed before junctions: login gating checks junction
      // active only, so reactivating junctions first would open portal access even
      // if the carrier update subsequently fails.
      const carrierInput = sanitizeCarrier({ ...record, active: "true" });
      await API.graphql(graphqlOperation(updateCarrier, { input: carrierInput }));

      try {
        if (archivedUsers.length > 0) {
          await Promise.all(setActiveUserReloadYardCarriers(archivedUsers, "true"));
        }
      } catch (junctionError) {
        // Compensating rollback: revert carrier so it matches the still-inactive
        // junctions. If rollback fails, carrier appears active but junctions remain
        // inactive — users stay blocked, no security hole.
        try {
          const rollbackInput = sanitizeCarrier({ ...record, active: "false" });
          await API.graphql(graphqlOperation(updateCarrier, { input: rollbackInput }));
        } catch (_rollbackError) {
          // intentionally swallowed — benign failure state, outer catch will notify
        }
        throw junctionError;
      }

      // Restoring puts the carrier back in the active list the dropdown caches.
      invalidateCarrierList();
      notify("Carrier restored successfully.");
      redirect("/carriers");
    } catch (error) {
      logError("managerApp:carriers:ArchiveRestoreButton:handleRestore", error, notify);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        {...props}
        label={isArchived ? "Restore" : "Archive"}
        color="default"
        disabled={loading || (!isArchived && !pristine)}
        onClick={handleClick}
      >
        {isArchived ? <RollbackIcon /> : <ArchiveIcon />}
      </Button>
      <Confirm
        isOpen={open}
        loading={loading}
        title="Archive Confirmation"
        content={archiveMessage}
        onConfirm={handleArchive}
        onClose={handleDialogClose}
      />
    </>
  );
};
